import * as React from 'react';
import { FaFileUpload, FaFilePdf, FaFileImage, FaFileAlt, FaLink } from 'react-icons/fa';
import * as KendoLayout from '@progress/kendo-react-layout'
import { AccentButton } from '../controls/AccentButton';
import { accentUtils, from, showOK, showWait, t } from '../services/HelperService';
import { AccentDialog } from './AccentDialog';
import { AccentTokenCtrl } from '../controls/AccentTokenCtrl';
import { AccentUploadButton } from '../controls/AccentUpload';
import { AccentComboUnbound } from '../controls/unbound/AccentComboUnbound';
import { AccentTextUnbound } from '../controls/unbound/AccentTextUnBound';
import { AccentHtmlUnbound } from '../controls/unbound/AccentHtmlUnbound';
import { isMobileOnly, usePosition } from '../controls/AccentDisplay';
import { RTLDocumentGroupPreview } from '../controls/documents/DocumentPreview';
import { queryNoTracking } from '../services/DataService';
import { Settings } from '../services/AppSettingsService';
import { useValidation } from '../controls/bound/AccentFormCtrl';
import { AccentCheckUnbound } from '../controls/unbound/AccentCheckUnbound';
import { getUser } from '../services/UserService';
import { getContext, getContextData } from '../services/EntityContextService';
import { showDialog } from '../services/HelperService';



const DocumentSelectorDlg = ({ documents, currentSelection, onClose }) => {
    const [selected, setSelected] = React.useState(currentSelection || []);

    const handleCheckboxChange = (doc) => {
        if (selected.find(s => s.ID === doc.ID)) {
            setSelected(selected.filter(s => s.ID !== doc.ID));
        } else {
            setSelected([...selected, doc]);
        }
    };

    const getDocumentIcon = (displayType) => {
        const type = displayType?.toLowerCase() || '';
        if (type === 'pdf') return <FaFilePdf style={{ color: '#dc3545', fontSize: '1.2em' }} />;
        if (type === 'image') return <FaFileImage style={{ color: '#28a745', fontSize: '1.2em' }} />;
        if (type === 'link') return <FaLink style={{ color: '#007bff', fontSize: '1.2em' }} />;
        if (type === 'html') return <FaFileAlt style={{ color: '#ffc107', fontSize: '1.2em' }} />;
        return <FaFileAlt style={{ color: '#6c757d', fontSize: '1.2em' }} />;
    };

    const getDisplayTypeBadgeColor = (displayType) => {
        const type = displayType?.toLowerCase() || '';
        if (type === 'pdf') return { backgroundColor: '#dc3545', color: 'white' };
        if (type === 'image') return { backgroundColor: '#28a745', color: 'white' };
        if (type === 'link') return { backgroundColor: '#007bff', color: 'white' };
        if (type === 'html') return { backgroundColor: '#ffc107', color: 'black' };
        return { backgroundColor: '#6c757d', color: 'white' };
    };

    const doOK = () => {
        onClose({ selected });
    };

    return (
        <AccentDialog
            headerContent="Select Documents"
            onClose={onClose}
            relativeWidth={d => d.width * 0.5}
            relativeHeight={d => d.height * 0.7}
            actionsContent={[
                <AccentButton key={2} onClick={doOK}>OK</AccentButton>,
            ]}
        >
            <div style={{ overflowY: 'auto', height: '100%', padding: '8px' }}>
                {documents.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                        No documents available
                    </div>
                ) : (
                    documents.map(d => {
                        const isSelected = selected.some(item => item.ID === d.ID);
                        return (
                            <div
                                key={d.ID}
                                onClick={() => handleCheckboxChange(d)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '12px',
                                    marginBottom: '8px',
                                    backgroundColor: isSelected ? '#e7f3ff' : '#f8f9fa',
                                    border: `1px solid ${isSelected ? '#007bff' : '#dee2e6'}`,
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    boxShadow: isSelected ? '0 2px 4px rgba(0,123,255,0.2)' : 'none'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) {
                                        e.currentTarget.style.backgroundColor = '#e9ecef';
                                        e.currentTarget.style.borderColor = '#adb5bd';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) {
                                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                                        e.currentTarget.style.borderColor = '#dee2e6';
                                    }
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleCheckboxChange(d)}
                                    style={{
                                        marginRight: '12px',
                                        width: '18px',
                                        height: '18px',
                                        cursor: 'pointer',
                                        flexShrink: 0
                                    }}
                                />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '10px',
                                        marginBottom: '4px'
                                    }}>
                                        {getDocumentIcon(d.DisplayType)}
                                        <div style={{ 
                                            fontWeight: isSelected ? '600' : '500',
                                            fontSize: '15px',
                                            color: '#212529',
                                            flex: 1,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {d.Description}
                                        </div>
                                    </div>
                                    {(d.DisplayType || d.Tag || d.Url) && (
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '8px',
                                            marginLeft: '28px',
                                            flexWrap: 'wrap'
                                        }}>
                                            {d.DisplayType && (
                                                <span style={{
                                                    ...getDisplayTypeBadgeColor(d.DisplayType),
                                                    padding: '2px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: '11px',
                                                    fontWeight: '500',
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {d.DisplayType}
                                                </span>
                                            )}
                                            {d.Tag && (
                                                <span style={{
                                                    backgroundColor: '#17a2b8',
                                                    color: 'white',
                                                    padding: '2px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: '11px',
                                                    fontWeight: '500'
                                                }}>
                                                    {d.Tag}
                                                </span>
                                            )}                                            
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </AccentDialog>
    );
};

const showDocumentSelector = async (documents, currentSelection) => {    
    return showDialog(<DocumentSelectorDlg documents={documents} currentSelection={currentSelection} />);    
};

const CustomUploadButton = React.forwardRef((props, ref) => {
    const { onChange, documentQuery, ...otherProps } = props;

    const [documents, setDocuments] = React.useState(null);
    const [currentSelection, setCurrentSelection] = React.useState([]);

    const showDocuments = !accentUtils.isNull(documentQuery);
    const onAttachmentsChanged = (e) => {
        if (documents) {
            const selectedDocs = e
                .map(att => documents.find(doc => doc.ID === att.value))
                .filter(Boolean); // filter out undefineds
            setCurrentSelection(selectedDocs);
        }

        if (onChange) {
            onChange(e);
        }
    };

    const onShowDocuments = async () => {
        let docs = documents;
        if (!docs) {
            docs = await documentQuery();
            setDocuments(docs);
        }

        const result = await showDocumentSelector(docs, currentSelection);

        if (!result.canceled) {
            setCurrentSelection(result.selected);

            const allAttachmentsInUpload = ref.current.attachments;
            const uploadedFiles = allAttachmentsInUpload.filter(attachment =>
                !docs.some(doc => doc.ID === attachment.value)
            );

            const documentAttachments = result.selected.map(d => ({
                ...d,
                text: d.Description,
                value: d.ID,
                Name: d.Description,
                internal: true,
                DisplayType: d.DisplayType,
                Tag: d.Tag
            }));

            const newAttachments = [...uploadedFiles, ...documentAttachments];
            if (ref.current) {
                ref.current.setValue(newAttachments);
            }
            onChange(newAttachments);
        }
    }

    return (<div className="d-flex" width="100%">
        <div className="flex-fill">
            <AccentUploadButton
                ref={ref}
                onChange={onAttachmentsChanged}
                {...otherProps}
            />
        </div>
        {showDocuments && (
            <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '4px' }}>
                <button
                    type="button"
                    title="Select from documents"
                    onClick={onShowDocuments}
                    style={{
                        background: 'transparent',
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        borderRadius: '4px',
                        padding: '0',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100%',
                        outline: 'none'
                    }}
                >
                    <span style={{
                        border: '1px solid white',
                        borderRadius: '3px',
                        padding: '4px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <FaFileUpload />
                    </span>
                </button>
            </div>
        )}
    </div>);
});


const query_GenerateEmailTemplateContent = queryNoTracking("GenerateEmailTemplateContent", undefined, true);


const elementIDs = {
    body: "bodyCtrl",
    preview: "prevDocCtrl"
};


export function mapToBaseEmailViewModel(model, id, toDisplayName, other, internalDocID) {

    return {
        ID: id,
        TemplateID: model.TemplateID,
        ToDisplayName: toDisplayName,
        To: JSON.stringify(model.to),
        Subject: model.subject,
        Body: model.body,
        CopyMe: model.copyMe,
        DocumentParams: JSON.stringify(from(model.attachments).where(a => a.ID === internalDocID).select(a => a.Params).firstOrDefault()),
        DocumentCustomParams: JSON.stringify(from(model.attachments).where(a => a.ID === internalDocID).select(a => a.CustomParams).firstOrDefault()),
        InternalAttachments: JSON.stringify(from(model.attachments).where(a => a.ID !== internalDocID).select(a => a.ID).toArray()),
        ExternalAttachment: model.externalAttachments,
        ...other
    };

}


let sendInprogress = false;

export const EmailDlg = React.memo(props => {

    const uploadCtrl = React.useRef();

    const [previewShown, setPreviewShown] = React.useState(props.previewNotRequired);
    const [refreshKey, setRefreshKey] = React.useState(1);
    const [selectedTab, setSelectedTab] = React.useState(isMobileOnly ? 0 : 1);
    const [to, setTo] = React.useState(props.to ?? []);
    const [toData, setToData] = React.useState(props.to ?? []);
    const [templateID, setTemplateID] = React.useState(from(props.templates ?? []).firstOrDefault(t => t.ID === props.defaultTemplateID));
    const [attachments, setAttachments] = React.useState(props.attachments ?? []);
    const [subject, setSubject] = React.useState(null);
    const [body, setBody] = React.useState(null);
    const [copyMe, setCopyMe] = React.useState(false);
    const [documentsReady, setDocumentsReady] = React.useState((props.attachments?.length ?? 0) === 0);

    const documentQuery = props.documentQuery;

    const user = getUser();

    const pos = usePosition(selectedTab === 1 ? elementIDs.body : elementIDs.preview);

    const validation = useValidation(".emailDlg");

    const onToChanged = React.useCallback(e => {

        const newToItems = [];
        const newToData = [...toData];

        e.value.forEach(function (t) {

            var orgItem = from(toData ?? []).firstOrDefault(i => t.value === i.Email);

            if (accentUtils.isNull(orgItem)) {
                orgItem = { Name: t.text, Email: t.text };
                newToData.push(orgItem);
                setRefreshKey(k => k + 1);
            }

            newToItems.push(orgItem);

        });

        setTo(newToItems);
        setToData(newToData);



    }, [props, setTo, toData, setToData, setRefreshKey]);


    const getUrl = (doc) => {

        if (doc.Tag === "Sketch")
            return `/Download/DocumentSketchUri/?uri=${encodeURIComponent(doc.Url)}`;

        return `/Download/DocumentUri/?uri=${encodeURIComponent(doc.Url)}`;

    };

    const onAttachmentsChanged = React.useCallback(e => {

        setAttachments(attachments => {
            // Keep existing attachments that match items in e
            const keptAttachments = attachments.filter(a =>
                from(e).any(x => x.value === a.ID)
            );

            // Add new attachments for items in e that have internal === true and don't already exist
            const newInternalAttachments = from(e)
                .where(x => x.internal === true && !attachments.some(a => a.ID === x.value))
                .select(x => ({
                    ID: x.value,
                    Name: x.Name || x.text,
                    ReadOnly: x.readOnly || false,
                    Url: getUrl(x),
                    DisplayType: x.DisplayType
                }))
                .toArray();

            return [...keptAttachments, ...newInternalAttachments];
        });

    }, [setAttachments]);


    const displayTemplate = React.useCallback(async templateID => {

        const done = showWait();

        setTemplateID(templateID);


        if (accentUtils.isNull(templateID)) {
            await done();
            return;
        }

        const context = await getContextData(getContext());

        const args = {
            UseBody: true,
            TemplateID: templateID,
            ActivityID: context.ActivityID,
            ContactID: context.ContactID,
            JobID: context.JobID,
            OpportunityID: context.OpportunityID,
            StatementID: context.CompanyID,
            SupplyJobID: context.PurchaseOrderID,
            InvoiceID: context.InvoiceID,
            WebLeadID: context.WebLeadID
        };

        const templateArgs = props.templateArgs;
        if (!accentUtils.isNull(templateArgs)) {
            if (!accentUtils.isNull(templateArgs.JobID)) args.JobID = templateArgs.JobID;
            if (!accentUtils.isNull(templateArgs.ContactID)) args.ContactID = templateArgs.ContactID;
        }

        const data = await query_GenerateEmailTemplateContent.getFirstOrDefaultWithCache(args)

        setSubject(data.Subject);
        setBody(data.Body);
        setRefreshKey(k => k + 1);


        done();

    }, [props.templateArgs, setRefreshKey, setSubject, setBody, setTemplateID]);

    const getSelectedTemplateId = React.useCallback(() => {
        if (accentUtils.isNull(templateID)) return null;
        // templateID is sometimes the full object (initial value) and sometimes the numeric ID (after selection)
        return accentUtils.isNull(templateID.ID) ? templateID : templateID.ID;
    }, [templateID]);


    const sendEmail = React.useCallback(async e => {

        if (accentUtils.isEmpty(body)) {
            showOK("application_strings.views.sendEmailDialog.titleResult", "Email body is required.", ["application_strings.application.buttons.ok"]).then(() => {
                e.promise();
            });
            return;
        }

        const showSendError = error => {
            const msg = error?.message ?? error?.Response ?? "Email not sent.";
            showOK("application_strings.views.sendEmailDialog.titleResult", msg, ["application_strings.application.buttons.ok"]).then(() => {
                e.promise();
                sendInprogress = false;
            });
        }

        let files = null;
        try {
            files = await uploadCtrl.current.getFilesForUpload();
        } catch (error) {
            showSendError(error);
            return;
        }

        const result = {
            TemplateID: getSelectedTemplateId(),
            to: to,
            subject: subject,
            body: body,
            attachments: [...attachments],
            externalAttachments: files.files,
            copyMe: copyMe
        }

        sendInprogress = true;

        Promise.resolve()
            .then(() => props.onSend(result))
            .then(sendResult => {



            if (sendResult.retry) {
                sendEmail(e);
            } else if (!accentUtils.isNull(sendResult.error)) {
                showOK(sendResult.error.title, sendResult.error.msg, ["application_strings.application.buttons.ok"]).then(() => {
                    e.promise();
                    sendInprogress = false;
                });
            } else {

                showOK("application_strings.views.sendEmailDialog.titleResult", "application_strings.views.sendEmailDialog.sendEmailSuccess", ["application_strings.application.buttons.ok"], false, null, true, 1024).then(() => {

                    sendInprogress = false;
                    props.onClose({});
                    e.promise();

                });
            }
            }).catch(showSendError);
    }, [props, subject, body, attachments, copyMe, to, sendInprogress, getSelectedTemplateId]);

    const onTemplateChanged = async e => {

        const newTemplateID = e;

        await displayTemplate(newTemplateID);

        if (!accentUtils.isEmpty(props.emailTemplateAppSettingsField)) {
            Settings.SetAppSettings({ [props.emailTemplateAppSettingsField]: newTemplateID });
        }


    };


    const getAIContext = React.useCallback(async e => {

        if (props.getAIContext) {
            return await props.getAIContext({
                subject: subject,
                body: body
            });
        }

    }, [props, subject, body]);

    const handleSelect = React.useCallback(e => {

        setSelectedTab(e.selected);

        if (e.selected === (isMobileOnly ? 1: 2)) {
            setPreviewShown(true);
        }

    }, [setSelectedTab, setPreviewShown]);

    const showPreview = e => {
        handleSelect({ selected: isMobileOnly ? 1 : 2 });

        e.promise();

    };


    const onDocumentsReady = React.useCallback(e => {

        setDocumentsReady(true);

    }, [documentsReady, setDocumentsReady]);


    React.useEffect(() => {

        if (accentUtils.isEmpty(templateID)) {
            if (!accentUtils.isEmpty(props.emailTemplateAppSettingsField)) {
                const defaultTemplateID = Settings.GetAppSettings()[props.emailTemplateAppSettingsField];

                if (!accentUtils.isEmpty(defaultTemplateID)) {
                    onTemplateChanged(defaultTemplateID)
                }

            }
        }


    },[]);


    const toItems = to.map(function (r) {

        return {
            text: r.Name + ' ' + '(' + (accentUtils.isEmpty(r.Email?.trim()) ? t("application_strings.application.general.emailMissing") : r.Email) + ')',
            value: r.Email,
            error: accentUtils.isEmpty(r.Email?.trim()) ? true : false
        };
    });

    const toDataItems = toData.map(function (r) {
        return {
            text: r.Name + ' ' + '(' + (accentUtils.isEmpty(r.Email?.trim()) ? t("application_strings.application.general.emailMissing") : r.Email) + ')',
            value: r.Email,
            error: accentUtils.isEmpty(r.Email?.trim()) ? true : false
        };
    });


    var attchedItems = attachments.map(function (a) {
        return {
            text: a.Name,
            value: a.ID,
            readOnly: a.ReadOnly
        };
    });

    const hasAttachments = attachments.length > 0;
    const bodyIsValid = !accentUtils.isEmpty(body);
    const allowSend = validation.isValid && bodyIsValid && ((documentsReady && !sendInprogress) || (props.previewNotRequired));


    const height = pos.toScreenHeight - (selectedTab ===1 ? 90 : 20);


    const btn = previewShown || !hasAttachments
        ? <AccentButton key="2" disabled={!allowSend} tagName="sendEmail" onClick={sendEmail}>{t("application_strings.application.buttons.send")}</AccentButton>
        : <AccentButton key="3"  tagName="showPreview" onClick={showPreview}>{t("application_strings.application.buttons.preview")}</AccentButton>

    return <AccentDialog
        fullscreen
        onClose={ props.onClose}
        actionsContent={[btn]}
    >

        <KendoLayout.TabStrip className="emailDlg" selected={selectedTab} onSelect={handleSelect} keepTabsMounted>
            {!isMobileOnly && <KendoLayout.TabStripTab title={t(props.title)} disabled></KendoLayout.TabStripTab>}

            <KendoLayout.TabStripTab title={t(isMobileOnly ? "application_strings.wizards.sendEmail.email.titleMobile" : "application_strings.wizards.sendEmail.email.title")}>
                <AccentTokenCtrl key={`e_${refreshKey}`} required value={toItems} data={toDataItems} onChange={onToChanged} label="application_strings.application.general.emailTo" />
                <CustomUploadButton ref={uploadCtrl} documentQuery={documentQuery} multiple={true} onChange={onAttachmentsChanged} defaultAttachments={attchedItems} label="application_strings.application.general.emailAttachments" />
                {(props.templates?.length ?? 0) > 0 && <AccentComboUnbound key={`t_${refreshKey}`} displayField="Description" valueField="ID" defaultValue={templateID} onChange={onTemplateChanged} data={props.templates} label="application_strings.application.general.emailTemplate" />}
                <AccentTextUnbound key={`s_${refreshKey}`} required defaultValue={subject} label="application_strings.application.general.emailSubject" onChange={setSubject} maxLength={ 250 } />
                <div id={elementIDs.body} >
                    <AccentHtmlUnbound key={`b_${refreshKey}`} required defaultValue={body} label="application_strings.application.general.emailBody" height={height} onChange={setBody} minTools={isMobileOnly} showAI={props.getAIContext} getAIContext={getAIContext} />
                </div>
                <AccentCheckUnbound label={t("application_strings.application.general.emailCopyMe", { email: user.Email })} onChange={e=> setCopyMe(e.checked) } />
            </KendoLayout.TabStripTab>

            {hasAttachments && <KendoLayout.TabStripTab title={t(isMobileOnly ? "application_strings.wizards.sendEmail.preview.titleMobile":  "application_strings.wizards.sendEmail.preview.title")}>
                <RTLDocumentGroupPreview elementID={elementIDs.preview} documents={attachments} maxHeight={height} onReady={ onDocumentsReady } />
            </KendoLayout.TabStripTab>}
        </KendoLayout.TabStrip>

    </AccentDialog>;

});

